import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import AuthGate from './components/AuthGate';
import ErrorBoundary from './components/ErrorBoundary';
import AppShell from './components/Layout/AppShell';
import ModuleRoute from './components/Layout/ModuleRoute';
import DevOnlyRoute from './components/Layout/DevOnlyRoute';
import { initTelemetry } from './utils/telemetry';
import useIdentityStore from './stores/useIdentityStore';
import { storage } from './utils/storage';

// All page routes are lazy-loaded for optimal bundle splitting
const Home = lazy(() => import('./pages/Home'));
const Today = lazy(() => import('./pages/Today'));
const QuickCapturePage = lazy(() => import('./pages/QuickCapturePage'));
const Focus = lazy(() => import('./pages/Focus'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Inbox = lazy(() => import('./pages/Inbox'));
const Identities = lazy(() => import('./pages/Identities'));
const Projects = lazy(() => import('./pages/Projects'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const AllTasks = lazy(() => import('./pages/AllTasks'));
const Sessions = lazy(() => import('./pages/Sessions'));

const Resources = lazy(() => import('./pages/Resources'));
const Areas = lazy(() => import('./pages/Areas'));
const Archives = lazy(() => import('./pages/Archives'));
const CalendarPage = lazy(() => import('./pages/Calendar'));
const Reviews = lazy(() => import('./pages/Reviews'));
const WeeklyReview = lazy(() => import('./pages/WeeklyReview'));
const QuarterlyReview = lazy(() => import('./pages/QuarterlyReview'));
const Gamification = lazy(() => import('./pages/Gamification'));
const Quests = lazy(() => import('./pages/Quests'));
const Vision = lazy(() => import('./pages/Vision'));
const Settings = lazy(() => import('./pages/Settings'));
const Store = lazy(() => import('./pages/Store'));
const DesignSystem = lazy(() => import('./pages/DesignSystem'));
const CeremonyRunner = lazy(() => import('./pages/CeremonyRunner'));
const TimedRunner = lazy(() => import('./pages/TimedRunner'));
const Onboarding = lazy(() => import('./pages/Onboarding'));

// Desktop specific
const TauriMiniPlayerWindow = lazy(() => import('./components/MiniPlayer/TauriMiniPlayerWindow'));

function LazyFallback() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', opacity: 0.5 }}>
      Loading…
    </div>
  );
}

/** OAuth callback landing — the popup reads this URL's params and auto-closes. */
function CalendarCallback() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: 'var(--space-xs)' }}>
      <p>Connecting Google Calendar...</p>
      <p style={{ fontSize: 'var(--font-xxs)', opacity: 0.5 }}>This window will close automatically.</p>
    </div>
  );
}

/**
 * Inner routes wrapped by AuthGate — everything that needs authentication.
 */
/** Redirect new users to /onboarding if they have no identities and haven't completed setup. */
function OnboardingRedirect() {
  const identities = useIdentityStore(s => s.identities);
  const location = useLocation();
  if (location.pathname === '/onboarding') return null;
  const completed = storage.get('onboarding_completed', false);
  if (identities.length === 0 && !completed) {
    return <Navigate to="/onboarding" replace />;
  }
  return null;
}

/** Wraps AppShell with onboarding guard — redirects new users before rendering the shell. */
function AppShellWithOnboardingGuard() {
  const redirect = OnboardingRedirect();
  if (redirect) return redirect;
  return <AppShell />;
}

function AuthenticatedRoutes() {
  return (
    <Routes>
      {/* Full-screen pages — outside AppShell */}
      <Route path="/onboarding" element={<Suspense fallback={<LazyFallback />}><Onboarding /></Suspense>} />
      <Route path="/weekly-review" element={<ModuleRoute module="reviews"><Suspense fallback={<LazyFallback />}><WeeklyReview /></Suspense></ModuleRoute>} />
      <Route path="/quarterly-review" element={<ModuleRoute module="reviews"><Suspense fallback={<LazyFallback />}><QuarterlyReview /></Suspense></ModuleRoute>} />
      <Route path="/rituals/:id/run" element={<Suspense fallback={<LazyFallback />}><CeremonyRunner /></Suspense>} />
      <Route path="/rituals/:id/timed" element={<Suspense fallback={<LazyFallback />}><TimedRunner /></Suspense>} />

      <Route element={<AppShellWithOnboardingGuard />}>
        {/* Core routes — always open, lazy loaded like all pages */}
        {/* Home v2 (2026-08-30) — the AI-first landing screen. Today keeps every one
            of its regions and moves to /today; nothing was removed. */}
        <Route path="/" element={<Suspense fallback={<LazyFallback />}><Home /></Suspense>} />
        <Route path="/today" element={<Suspense fallback={<LazyFallback />}><Today /></Suspense>} />
        <Route path="/quick-capture" element={<Suspense fallback={<LazyFallback />}><QuickCapturePage /></Suspense>} />
        <Route path="/focus" element={<Suspense fallback={<LazyFallback />}><Focus /></Suspense>} />
        <Route path="/circuits" element={<Suspense fallback={<LazyFallback />}><Focus /></Suspense>} />
        <Route path="/rituals" element={<ModuleRoute module="rituals"><Suspense fallback={<LazyFallback />}><Focus /></Suspense></ModuleRoute>} />
        {/* Legacy /flows alias → /circuits */}
        <Route path="/flows" element={<Navigate to="/circuits" replace />} />

        {/* Ambient-core routes — header/command-reached, not gated */}
        <Route path="/inbox" element={<Suspense fallback={<LazyFallback />}><Inbox /></Suspense>} />
        <Route path="/identities" element={<Suspense fallback={<LazyFallback />}><Identities /></Suspense>} />
        <Route path="/tasks" element={<Suspense fallback={<LazyFallback />}><AllTasks /></Suspense>} />
        <Route path="/sessions" element={<Suspense fallback={<LazyFallback />}><Sessions /></Suspense>} />

        {/* Gateable routes — wrapped in ModuleRoute */}
        <Route path="/dashboard" element={<ModuleRoute module="dashboard"><Suspense fallback={<LazyFallback />}><Dashboard /></Suspense></ModuleRoute>} />
        <Route path="/projects" element={<ModuleRoute module="projects"><Suspense fallback={<LazyFallback />}><Projects /></Suspense></ModuleRoute>} />
        <Route path="/projects/:id" element={<ModuleRoute module="projects"><Suspense fallback={<LazyFallback />}><ProjectDetail /></Suspense></ModuleRoute>} />
        <Route path="/projects/tasks" element={<ModuleRoute module="projects"><Suspense fallback={<LazyFallback />}><AllTasks /></Suspense></ModuleRoute>} />
        <Route path="/resources" element={<ModuleRoute module="resources"><Suspense fallback={<LazyFallback />}><Resources /></Suspense></ModuleRoute>} />
        <Route path="/areas" element={<ModuleRoute module="areas"><Suspense fallback={<LazyFallback />}><Areas /></Suspense></ModuleRoute>} />
        <Route path="/archives" element={<ModuleRoute module="archives"><Suspense fallback={<LazyFallback />}><Archives /></Suspense></ModuleRoute>} />
        <Route path="/calendar" element={<ModuleRoute module="calendar"><Suspense fallback={<LazyFallback />}><CalendarPage /></Suspense></ModuleRoute>} />
        <Route path="/calendar/callback" element={<CalendarCallback />} />
        <Route path="/gamification" element={<Suspense fallback={<LazyFallback />}><Gamification /></Suspense>} />
        <Route path="/quests" element={<ModuleRoute module="quests"><Suspense fallback={<LazyFallback />}><Quests /></Suspense></ModuleRoute>} />
        <Route path="/vision" element={<ModuleRoute module="vision"><Suspense fallback={<LazyFallback />}><Vision /></Suspense></ModuleRoute>} />
        <Route path="/reviews" element={<ModuleRoute module="reviews"><Suspense fallback={<LazyFallback />}><Reviews /></Suspense></ModuleRoute>} />

        {/* Home is the single AI surface — the old page's thread view is reused there
            (AICoach's exported ChatPanel), so this route would be a second front door. */}
        <Route path="/ai-coach" element={<Navigate to="/" replace />} />
        {/* Store — CORE/ungated: always available, never wrapped in ModuleRoute */}
        <Route path="/store" element={<Suspense fallback={<LazyFallback />}><Store /></Suspense>} />
        <Route path="/settings" element={<Suspense fallback={<LazyFallback />}><Settings /></Suspense>} />
        {/* Developer surface — a component/token showcase, not a product page.
            DevOnlyRoute redirects home in a production build; see Sidebar.jsx
            for the matching dev-only nav row that links here. */}
        <Route path="/design-system" element={<DevOnlyRoute><Suspense fallback={<LazyFallback />}><DesignSystem /></Suspense></DevOnlyRoute>} />

        {/* Redirect aliases */}
        <Route path="/achievements" element={<Navigate to="/gamification?tab=progress" replace />} />
        <Route path="/rewards" element={<Navigate to="/gamification?tab=shop" replace />} />
        <Route path="/stats" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  useEffect(() => {
    initTelemetry();

    // Global error handlers — log uncaught errors for debugging
    const handleError = (event) => {
      console.error('[Global Error]', event.error || event.message);
    };
    const handleRejection = (event) => {
      console.error('[Unhandled Rejection]', event.reason);
    };
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <Routes>
          {/* Desktop mini-player: Isolated window — reads localStorage + BroadcastChannel only.
              Must be OUTSIDE AuthGate to avoid Firestore rehydration overwriting live flow state. */}
          <Route path="/mini-player" element={<Suspense fallback={<LazyFallback />}><TauriMiniPlayerWindow /></Suspense>} />

          {/* All other routes require authentication */}
          <Route path="/*" element={<AuthGate><AuthenticatedRoutes /></AuthGate>} />
        </Routes>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
